import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, auth } from '../firebase/config'; // Importamos 'auth' desde tu configuración central
import { onAuthStateChanged } from 'firebase/auth'; // Importamos el listener de estado de autenticación
import { collection, onSnapshot, query, where } from 'firebase/firestore';

// Creamos el contexto maestro
const SegurosDataContext = createContext(null);

/**
 * Proveedor de datos maestros de Seguros con Sincronización en Tiempo Real (onSnapshot).
 * Corregido: Ahora espera de forma segura a que la sesión de Firebase Auth esté resuelta
 * antes de intentar escuchar colecciones de Firestore para prevenir errores de "Missing or insufficient permissions".
 */
export const SegurosDataProvider = ({ children }) => {
    const [dbMasters, setDbMasters] = useState({
        canales: [],
        companias: [],
        colaboradores: [],
        contadores: [],
        acuerdos: [],
        riesgos: [],
        reglasRenovacion: []
    });

    const [loadingMasters, setLoadingMasters] = useState(true);

    useEffect(() => {
        // Guardamos las funciones de desuscripción de Firestore para poder apagarlas en caliente
        let activeUnsubscribes = [];

        const cleanActiveListeners = () => {
            activeUnsubscribes.forEach(unsub => {
                if (typeof unsub === 'function') unsub();
            });
            activeUnsubscribes = [];
        };

        // --- ESCUDO DE AUTENTICACIÓN (onAuthStateChanged) ---
        // Este observador garantiza que las consultas a base de datos solo se ejecuten
        // cuando Firebase Auth ha validado que el token del usuario está activo y listo.
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            // Cada vez que el estado de autenticación cambie, limpiamos listeners anteriores
            cleanActiveListeners();

            if (!user) {
                // Si no hay usuario logueado (sesión cerrada o cargando el login):
                // 1. Limpiamos los datos de memoria por seguridad.
                setDbMasters({
                    canales: [],
                    companias: [],
                    colaboradores: [],
                    contadores: [],
                    acuerdos: [],
                    riesgos: [],
                    reglasRenovacion: []
                });
                // 2. Liberamos la carga para permitir que se monte la pantalla de Login.
                setLoadingMasters(false);
                return;
            }

            // SI HAY UN USUARIO ACTIVO (Autenticado de forma anónima, custom token o email/pass)
            // Activamos la pantalla de carga maestro mientras se realiza la sincronización inicial
            setLoadingMasters(true);

            // Objeto de control para garantizar que todas las listas hayan emitido
            // su snapshot inicial antes de liberar la pantalla de carga
            const initialLoadTracker = {
                canales: false,
                companias: false,
                colaboradores: false,
                contadores: false,
                acuerdos: false,
                riesgos: false,
                reglasRenovacion: false
            };

            const checkAllLoaded = () => {
                const allLoaded = Object.values(initialLoadTracker).every(Boolean);
                if (allLoaded) {
                    setLoadingMasters(false);
                }
            };

            // 1. Escuchar Canales en tiempo real
            const unsubCanales = onSnapshot(collection(db, 'public/data/canales'), (snap) => {
                setDbMasters(prev => ({
                    ...prev,
                    canales: snap.docs.map(d => ({ 
                        id: d.id, 
                        ...d.data(), 
                        nombreDisplay: d.data().nombre || d.data().subcanal 
                    }))
                }));
                initialLoadTracker.canales = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (canales):", err);
                initialLoadTracker.canales = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubCanales);

            // 2. Escuchar Compañías Aseguradoras
            const unsubCompanias = onSnapshot(collection(db, 'public/data/companias'), (snap) => {
                setDbMasters(prev => ({
                    ...prev,
                    companias: snap.docs.map(d => ({ id: d.id, ...d.data() }))
                }));
                initialLoadTracker.companias = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (companias):", err);
                initialLoadTracker.companias = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubCompanias);

            // 3. Escuchar Colaboradores Activos
            const qColaboradores = query(collection(db, 'public/data/colaboradores'), where('activo', '==', true));
            const unsubColaboradores = onSnapshot(qColaboradores, (snap) => {
                setDbMasters(prev => ({
                    ...prev,
                    colaboradores: snap.docs.map(d => ({ id: d.id, ...d.data() }))
                }));
                initialLoadTracker.colaboradores = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (colaboradores):", err);
                initialLoadTracker.colaboradores = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubColaboradores);

            // 4. Escuchar Contadores
            const unsubContadores = onSnapshot(collection(db, 'public/data/contadores'), (snap) => {
                setDbMasters(prev => ({
                    ...prev,
                    contadores: snap.docs.map(d => ({ id: d.id, ...d.data() }))
                }));
                initialLoadTracker.contadores = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (contadores):", err);
                initialLoadTracker.contadores = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubContadores);

            // 5. Escuchar Acuerdos de comisiones
            const unsubAcuerdos = onSnapshot(collection(db, 'public/data/acuerdos'), (snap) => {
                setDbMasters(prev => ({
                    ...prev,
                    acuerdos: snap.docs.map(d => ({ id: d.id, ...d.data() }))
                }));
                initialLoadTracker.acuerdos = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (acuerdos):", err);
                initialLoadTracker.acuerdos = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubAcuerdos);

            // 6. Escuchar Catálogo de Riesgos/Ramos
            const unsubRiesgos = onSnapshot(collection(db, 'public/data/riesgos'), (snap) => {
                setDbMasters(prev => ({
                    ...prev,
                    riesgos: snap.docs.map(d => ({ id: d.id, ...d.data() }))
                }));
                initialLoadTracker.riesgos = true;
                checkAllLoaded();
            }, (err) => {
                console.error("Error en tiempo real (riesgos):", err);
                initialLoadTracker.riesgos = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubRiesgos);

            // 7. Escuchar Reglas de Renovación de Patrimoniales
            const unsubReglas = onSnapshot(collection(db, 'public/data/reglas_renovacion'), (snap) => {
                setDbMasters(prev => ({
                    ...prev,
                    reglasRenovacion: snap.docs.map(d => ({ id: d.id, ...d.data() }))
                }));
                initialLoadTracker.reglasRenovacion = true;
                checkAllLoaded();
            }, (err) => {
                console.warn("Reglas de renovación no inicializadas o vacías en Firestore:", err.message);
                initialLoadTracker.reglasRenovacion = true;
                checkAllLoaded();
            });
            activeUnsubscribes.push(unsubReglas);
        });

        // Cleanup final: Desuscribimos la escucha de autenticación y todos los streams activos de Firestore
        return () => {
            unsubscribeAuth();
            cleanActiveListeners();
        };
    }, []);

    return (
        <SegurosDataContext.Provider value={{ dbMasters, loadingMasters }}>
            {children}
        </SegurosDataContext.Provider>
    );
};

export const useSegurosData = () => {
    const context = useContext(SegurosDataContext);
    if (!context) {
        throw new Error("useSegurosData debe ser utilizado obligatoriamente dentro de un SegurosDataProvider");
    }
    return context;
};